import React, { useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip.tsx';
import ActionMenu from '@components/RingilComponents/ActionMenu.tsx';
import { useSecurityMode } from '@components/security/SecurityModeContext';
import SvgDots from '@icons/3-dots-horizontal-inverted.svg?react';
import { massActionSecuredConfig, shownMassActions } from '../massActionSecurity';
import type { MassActionConfig } from '../massActionTypes';
import { visibleActionCount } from './bulkActionsLayout';

interface ComponentProps<T> {
  massActionConfig: MassActionConfig<T>[];
  selectedObjects: T[];
  /** Table identity, so fallback area ids stay unique when a page holds more than one table. */
  idPrefix: string;
  /** Mass actions shown at all (a table can have only the right-hand controls). */
  hasMassActions: boolean;
  /** Always-available controls on the right — export and reload act on the filter, not on a selection. */
  rightSlot?: React.ReactNode;
}

const GAP = 8;
const MORE_BUTTON_WIDTH = 32;

// CO Button Solid Small: 12px label, 12px icon, 4/8 padding — on top of RingilButton's filled rectangle.
const smallButton = 'h-6 gap-1.5 px-2 py-1 rounded text-xs leading-4 shadow-none [&_svg]:w-3 [&_svg]:h-3';

/**
 * The bulk-actions bar of RingilDataTable v3 (Figma CO Table Bulk Actions 170:13141): always above the
 * header, showing how many rows are selected and the actions for them — disabled, with the reason on
 * hover, while nothing is selected. Actions that don't fit move into a "…" menu; export and reload
 * stay on the right whatever the width.
 */
const BulkActionsBar = <T,>({ massActionConfig, selectedObjects, idPrefix, hasMassActions, rightSlot }: ComponentProps<T>) => {
  const { tr } = useTranslationLgs();
  const { enabled: securityMode } = useSecurityMode();
  const actions = hasMassActions ? shownMassActions(massActionConfig, selectedObjects, securityMode) : [];
  const count = selectedObjects.length;

  const slotRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [fits, setFits] = useState(actions.length);

  // A hidden copy of every button gives their natural widths, so the visible row can show as many as
  // fit and fold the rest — including ones that are folded right now and so can't be measured in place.
  useLayoutEffect(() => {
    const slot = slotRef.current;
    const measure = measureRef.current;
    if (!slot || !measure) return;
    const update = () => {
      const widths = Array.from(measure.children).map(child => (child as HTMLElement).offsetWidth);
      setFits(visibleActionCount(widths, slot.clientWidth, GAP, MORE_BUTTON_WIDTH));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(slot);
    return () => observer.disconnect();
  }, [actions.length, count]);

  const disabledOf = (item: MassActionConfig<T>) => (item.disabled ? item.disabled(selectedObjects) : count === 0);
  // An empty selection is why most actions are disabled; an action disabled for its own reason says so itself.
  const reasonOf = (item: MassActionConfig<T>) =>
    disabledOf(item) ? item.disabledReason?.(selectedObjects) ?? (count === 0 ? tr('RingilDataTable.selectRowsFirst', 'Nejprve vyberte řádky') : undefined) : undefined;

  const button = (item: MassActionConfig<T>, idx: number) => {
    const disabled = disabledOf(item);
    return (
      <RingilButton
        label={item.label}
        icon={item.icon}
        mode="filled-rectangle"
        onClick={() => item.onClick()}
        disabled={disabled}
        secured={massActionSecuredConfig(item, { idx, idPrefix, selected: selectedObjects, securityMode, disabled })}
        className={smallButton}
      />
    );
  };

  const shown = actions.slice(0, fits);
  const folded = actions.slice(fits);

  return (
    <div
      className="flex items-center gap-3 px-3 py-2 bg-content-quaternary border-0 border-b border-solid border-content-negativePrimary font-['DM_Sans']"
      data-testid={`${idPrefix}-bulk-actions`}
    >
      {hasMassActions && (
        <span className="shrink-0 text-sm font-medium leading-5 text-content-primary whitespace-nowrap">
          {count} {tr('RingilDataTable.selected', 'selected')}
        </span>
      )}
      <div ref={slotRef} className="relative flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
        {shown.map((item, idx) => {
          const id = `${idPrefix}-bulk-${idx}`;
          const reason = reasonOf(item);
          return (
            // The tooltip hangs on a wrapper: a disabled button fires no mouse events.
            <span key={id} id={id} className={clsx('inline-flex shrink-0', reason && 'cursor-help')}>
              {button(item, idx)}
              {reason && <CustomTooltip targetSelector={`#${id}`} content={reason} />}
            </span>
          );
        })}
        {folded.length > 0 && (
          <ActionMenu
            items={folded.map((item, i) => ({
              key: `${idPrefix}-bulk-more-${i}`,
              label: item.label,
              icon: item.icon,
              onClick: item.onClick,
              disabled: disabledOf(item),
              disabledReason: reasonOf(item),
            }))}
            icon={<SvgDots className="w-3 h-3" />}
            mode="table"
            density="compact"
            tooltip={tr('generic.moreActions', 'More actions')}
            triggerClassName={clsx(smallButton, 'w-auto bg-content-brand border-content-brand text-content-negativePrimary')}
            dataTestId={`${idPrefix}-bulk-more`}
          />
        )}
        <div ref={measureRef} aria-hidden className="pointer-events-none invisible absolute left-0 top-0 flex gap-2">
          {actions.map((item, idx) => (
            <span key={idx} className="inline-flex shrink-0">
              {button(item, idx)}
            </span>
          ))}
        </div>
      </div>
      {rightSlot && <div className="ml-auto flex shrink-0 items-center gap-2">{rightSlot}</div>}
    </div>
  );
};

export default BulkActionsBar;
